// @ts-check
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';

// core
import {
  AppContext,
  Breadcrumb,
  CheckBoxLabel,
  ClipboardCopy,
  cloneCampaignAndNavigateToDetails,
  CloneCampaignFlowModal,
  CsvDataTable,
  OPEN_MODE,
  PAGE_STEP,
  PageTitle,
  processQuery,
  QueryFilterBuilder,
  QueryFilterPreview,
  resolveRouteWithId,
  routerUrl,
  scrollTopPage,
  useFeatureFlag,
  useScrollTop,
} from 'core';
import { previewTableConfig } from 'core/components/Tables/configs';

// state
import {
  useStateCampaign,
  useStateExportFilters,
  useStateFilters,
} from 'core/hooks/use-state-campaign';
import { campaignRefresh } from 'core/services/campaign-refresh-service';
import { useAppDispatch, useAppSelector } from 'core/store';
import {
  cloneCampaignAsync,
  exportCampaignAsync,
  publishCampaignAsync,
  resetAlertMessages,
  resetCampaign,
  resetCampaignPreview,
  setCampaignData,
  setExportInProgress,
  setInfoMessage,
  setPageLoading,
  setPageSummary,
  setSelectedRoute,
  setStatementPreviewAsync,
} from 'core/store/slices';

// components
import { DetailList } from './components/DetailList';
import { ExportFileSummary } from './components/ExportFileSummary';

const CampaignIdPanel = styled.div`
  border: 1px solid #d0d0d0;
`;

function CampaignDetails() {
  const { sendMessage, stopSendMessage } = useContext(AppContext);
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation();
  const { id: routeCampaignId } = /** @type {{ id?: string }} */ (useParams());
  // Use separate selectors to avoid returning a new combined object reference every render.
  const campaignData = useAppSelector((state) => state.campaignData);
  const exportFilter = useAppSelector((state) => state.exportFilter);
  const queryBuilder = useAppSelector((state) => state.queryBuilder);
  const campaignDownload = useAppSelector((state) => state.campaignDownload);
  const campaignPreview = useAppSelector((state) => state.campaignPreview);

  const { loading: campaignLoading, campaign, openMode } = campaignData;
  const { calculatedMatchSummary } = campaign;
  const { exportFilters, loading: exportFiltersLoading } = exportFilter;
  const { filters } = queryBuilder;
  const { exportInProgress, downloadInProgress, downloadEnd } =
    campaignDownload;
  const {
    loading: previewDataLoading,
    websocketLoading,
    previewData,
  } = campaignPreview;

  const isCloningEnabled = useFeatureFlag(
    'ACAN_CPBD_CLONING_INTEGRATION_387594',
  );

  const [cloneModalOpen, setCloneModalOpen] = useState(false);

  const closeCloneModal = () => {
    setCloneModalOpen(false);
  };

  /**
   * Wrapper to keep callsites explicit and small.
   * @param {string} sourceCampaignId
   * @param {string} cloneName
   */
  const cloneToDetails = (sourceCampaignId, cloneName) =>
    cloneCampaignAndNavigateToDetails({
      dispatch,
      history,
      sourceCampaignId,
      cloneName,
      cloneCampaignAsync,
      resetAlertMessages,
      resetCampaignPreview,
      onClose: closeCloneModal,
      onAfterCloneSuccess: () => {
        dispatch(resetCampaign());
      },
      navigationMethod: 'replace',
    });

  const lastCloneRefreshedCampaignIdRef = useRef(null);

  /**
   * Clone the selected campaign using the provided name, then navigate to the
   * newly-created campaign Details page.
   *
   * Note:
   * Clears any existing banner alerts before starting.
   * Errors are handled by the clone thunk (banner error state); simply
   * short-circuit on failure.
   * Intentionally skip a list refresh here; the user is taken directly to
   * the cloned campaign.
   * @param {string} cloneName
   */
  const handleCloneAndRefresh = async (cloneName) => {
    const sourceCampaignId = campaign?.campaignId;
    if (!sourceCampaignId) return;

    await cloneToDetails(sourceCampaignId, cloneName);
  };

  const cloneSourceIncludesFile = Boolean(campaign?.fileName);

  const latestStatement = campaign?.details?.statements?.length
    ? campaign.details.statements.reduce((latest, statement) => {
        if (!latest) return statement;
        return new Date(statement.started_at) > new Date(latest.started_at)
          ? statement
          : latest;
      }, null)
    : null;

  const hasPreviewData = previewData?.records?.length > 0;
  const columns = hasPreviewData ? previewData.columns : [];
  const records = hasPreviewData ? previewData.records : [];

  const totalRecordCount =
    previewData?.totalRecordCount && previewData.totalRecordCount > 0
      ? previewData.totalRecordCount
      : latestStatement?.record_count && latestStatement.record_count > 0
      ? latestStatement.record_count
      : 0;

  const locationState = /** @type {any} */ (location.state);
  const shouldRefreshAfterClone = Boolean(locationState?.refreshAfterClone);

  useStateExportFilters();
  useStateFilters();
  useStateCampaign();
  useScrollTop();

  useEffect(() => {
    dispatch(setSelectedRoute(routerUrl.CAMPAIGN_DETAILS));
    dispatch(setPageSummary(null));
    dispatch(setCampaignData({ openMode: OPEN_MODE.VIEW }));
  }, [dispatch]);

  useEffect(() => {
    // When arriving at Details right after a clone, automatically refresh (run) the
    // campaign query and update the page fields (e.g. 'Last refreshed' date,
    // 'Records', latest underlying records, etc.).
    if (!shouldRefreshAfterClone) return;

    // Wait for campaign data to finish loading before attempting refresh.
    if (campaignLoading) return;

    if (!campaign?.campaignId || !campaign?.logic) return;

    // Ensure we're refreshing the campaign that matches the current route,
    // not a stale campaign from before navigation.
    if (campaign.campaignId !== routeCampaignId) return;

    if (lastCloneRefreshedCampaignIdRef.current === campaign.campaignId) return;

    lastCloneRefreshedCampaignIdRef.current = campaign.campaignId;
    campaignRefresh({
      campaign,
      sendMessage,
      dispatch,
      getCurrentCampaignId: () => routeCampaignId,
    });

    // Clear the navigation flag so refresh doesn't re-trigger on re-render.
    history.replace({
      ...location,
      state: {
        ...(locationState || {}),
        refreshAfterClone: undefined,
      },
    });
  }, [
    campaign,
    campaignLoading,
    dispatch,
    history,
    location,
    locationState,
    routeCampaignId,
    sendMessage,
    shouldRefreshAfterClone,
  ]);

  useEffect(() => {
    if (exportInProgress) {
      dispatch(setInfoMessage('The export of the campaign is in progress'));
      scrollTopPage();
    }
  }, [dispatch, exportInProgress]);

  useEffect(() => {
    if (downloadInProgress) {
      dispatch(setInfoMessage('The download of the campaign is in progress'));
    } else if (downloadEnd) {
      dispatch(setInfoMessage(''));
    }
  }, [dispatch, downloadEnd, downloadInProgress]);

  useEffect(() => {
    return () => {
      dispatch(resetAlertMessages());
    };
  }, [dispatch]);

  useEffect(() => {
    // this function is triggered when the Page component is unmounted
    // in this case it stops the websocket when it changes page
    return () => {
      stopSendMessage();
      dispatch(resetCampaignPreview());
    };
  }, [dispatch, stopSendMessage]);

  useEffect(() => {
    async function setStatementPreview() {
      dispatch(
        setStatementPreviewAsync({
          statementId: latestStatement.statement_id,
        }),
      );
    }

    if (latestStatement?.statement_id) {
      setStatementPreview();
    }
  }, [dispatch, latestStatement]);

  const computeQueryLogic = useMemo(
    () => processQuery(campaign.logic),
    [campaign.logic],
  );

  const downloadCampaign = (statementId = '', type = '') => {
    let downloadFilename = `${campaign.campaignName.replace(
      / /g,
      '_',
    )}_${new Date().toISOString().replace(/[:.]/g, '-')}`;

    downloadFilename += `${type === 'audit' ? '_audit' : ''}.csv`;

    dispatch(exportCampaignAsync({ statementId, type, downloadFilename }));
  };

  const exportList = (exportType = 'csv') => {
    if (!campaign.logic) return;

    dispatch(setPageLoading(true));
    dispatch(setExportInProgress(true));

    // Use the latest statement_id. Don't create a new one.
    if (latestStatement && latestStatement.statement_id) {
      downloadCampaign(latestStatement.statement_id);
    } else {
      dispatch(setInfoMessage('No valid statement ID found for export.'));
      dispatch(setPageLoading(false));
      dispatch(setExportInProgress(false));
    }
  };

  //TODO: Refactor. One function to handle both exportAuditFile and exportList.
  const exportAuditFile = () => {
    if (!campaign.logic) return;

    dispatch(setPageLoading(true));
    dispatch(setExportInProgress(true));

    // Use the latest statement_id. Don't create a new one.
    if (latestStatement && latestStatement.statement_id) {
      downloadCampaign(latestStatement.statement_id, 'audit');
    } else {
      dispatch(setInfoMessage('No valid statement ID found for audit export.'));
      dispatch(setPageLoading(false));
      dispatch(setExportInProgress(false));
    }
  };

  const publishCampaign = () => {
    dispatch(
      publishCampaignAsync({
        published: true,
        campaignId: campaign.campaignId,
      }),
    );
  };

  const detailClick = () => {
    if (campaign.published) return;

    dispatch(resetCampaignPreview());
    dispatch(
      setCampaignData({
        openMode: OPEN_MODE.UPDATE,
      }),
    );

    history.push(
      resolveRouteWithId(routerUrl.CAMPAIGN_UPDATE_DATA, campaign.campaignId),
    );
  };

  return (
    <section>
      <header>
        <PageTitle title="Campaign details" />
        <Breadcrumb
          currentPage={PAGE_STEP.CAMPAIGN_DETAILS}
          openMode={openMode}
        />
      </header>
      <section>
        <div className="d-flex justify-content-between">
          <div>
            <h3>{campaign.campaignName}</h3>
          </div>
          <CampaignIdPanel className="my-auto p-3">
            <div>
              <strong>Campaign ID:</strong>
            </div>
            <div>{campaign.campaignId}</div>
            <div className="mt-1">
              <ClipboardCopy textToCopy={campaign.campaignId} />
            </div>
            {isCloningEnabled ? (
              <div className="mt-2">
                <button
                  type="button"
                  className="btn btn-link p-0 fw-normal clone-campaign-link d-inline-flex align-items-center gap-1 text-decoration-underline"
                  data-ga-event="link"
                  data-ga-action="click"
                  data-ga-name="onclick"
                  data-ga-type="internal link"
                  data-ga-region="main content"
                  data-ga-section="Campaign Details"
                  data-ga-component=""
                  data-ga-text={`Clone campaign: ${campaign.campaignName}`}
                  aria-label={`Clone campaign ${campaign.campaignName}`}
                  onClick={() => {
                    setCloneModalOpen(true);
                  }}
                >
                  <i className="fa-regular fa-copy" aria-hidden="true" />
                  <span>Clone campaign</span>
                </button>
              </div>
            ) : null}
          </CampaignIdPanel>
        </div>
        <p>{campaign.campaignDescription}</p>
      </section>
      <section>
        <div className="row row-cols-2">
          <div className="col-6">
            <CheckBoxLabel
              title="Campaign Purposes"
              items={campaign.campaignPurpose.purposes}
              otherItem={campaign.campaignPurpose.other}
            />
          </div>
          <div className="col-6">
            <CheckBoxLabel
              title="Outreach Channels"
              items={campaign.outreachChannel.channels}
              otherItem={campaign.outreachChannel.other}
            />
          </div>
        </div>
      </section>
      <section>
        <DetailList
          loading={campaignLoading}
          campaign={campaign}
          latestStatement={latestStatement}
          exportFilters={exportFilters}
          exportFiltersLoading={exportFiltersLoading}
          exportInProgress={exportInProgress}
          isPreviewDataLoading={previewDataLoading || websocketLoading}
          totalRecordCount={totalRecordCount}
          sendMessage={sendMessage}
          onPublishCampaign={publishCampaign}
          onExportList={exportList}
          onExportAuditFile={exportAuditFile}
          onDetailClick={detailClick}
        />
      </section>
      <section className="mt-6">
        <ExportFileSummary
          totalRecords={calculatedMatchSummary?.totalRecords}
          unmatchedImportedFile={calculatedMatchSummary?.unmatchedImportedFile}
          unmatchedFiltered={calculatedMatchSummary?.unmatchedFiltered}
        />
      </section>
      <section className="mt-6 p-0 col-6">
        <header>
          <h4>Campaign Logic</h4>
        </header>
        <article className="">
          <p>Here you can see all filters applied to generate your campaign.</p>
        </article>
      </section>

      {campaign?.logic ? (
        <section>
          <QueryFilterBuilder
            valueQuery={campaign.logic}
            filters={filters}
            readOnly
          />
        </section>
      ) : null}

      <section className="mt-2">
        <QueryFilterPreview query={computeQueryLogic} />
      </section>

      <section className="mt-3">
        <CsvDataTable
          loading={previewDataLoading || websocketLoading}
          config={previewTableConfig}
          columns={columns}
          records={records}
          totalRecordCount={totalRecordCount}
          height="300px"
        />
      </section>

      {isCloningEnabled ? (
        <CloneCampaignFlowModal
          isOpen={cloneModalOpen}
          campaignName={campaign?.campaignName}
          sourceIncludesFile={cloneSourceIncludesFile}
          onCloneAndRefresh={handleCloneAndRefresh}
          onClose={closeCloneModal}
        />
      ) : null}
    </section>
  );
}

export { CampaignDetails };
